import camelcase from 'camelcase';

import { calculateColumnLetters } from '@pages/Filing/ConversionTool/MappingSteps/CTStepOne/components/ConversionSettingsForm/helpers/calculateColumnLetters';
import {
  COLUMN,
  FORMULA,
  NOT_PROVIDED,
  STATIC,
} from '@pages/Filing/ConversionTool/MappingSteps/CTStepOne/constants';

/**
 * @typedef {Object} Map
 * @property {string} type - unique identifier
 * @property {string} value - column value
 */

/**
 * @typedef {Object} Option
 * @property {string} display - display value
 * @property {string} optionType - field identifier
 */

/**
 * @typedef {Object} FieldValue
 * @property {string} id - unique identifier
 * @property {string} display - display value
 * @property {string} description - field description
 * @property {string} type - field type
 * @property {boolean} required - field type
 * @property {boolean} disabled - is field disabled
 * @property {boolean} selected - is field selected
 * @property {number|null} index - column index
 * @property {string} optionalValue - field optionalValue
 * @property {Array<Option>} options - select field options
 */

/**
 * Converts api data for ui representation in mapping edit page.
 * @param {Object<string, Map>} [columnData={}] = processed column data
 * @param {Object<string, FieldValue>} [mappingShape={}] = column shape from api
 * @returns {Object<string, FieldValue>}
 */
export const conversionToolMappingEditModel = (columnData = {}, mappingShape = {}) => {
  const initialValues = Object.values(mappingShape)?.reduce(
    (acc, curr) => {
      const id = camelcase(curr?.id);
      const value = acc[id]?.value;
      const type = acc[id]?.type;

      if (!acc?.hasOwnProperty(id)) {
        return {
          ...acc,
          [id]: {
            ...curr,
            value: NOT_PROVIDED,
            optionalValue: '',
            index: null,
          },
        };
      }

      const isNotNumber = Number.isNaN(parseFloat(value));

      return {
        ...acc,
        [id]: {
          ...curr,
          value: type === FORMULA ? value : type,
          optionalValue:
            isNotNumber && type !== FORMULA ? value : calculateColumnLetters(parseFloat(value)),
          index: isNotNumber ? null : parseFloat(value),
        },
      };
    },
    { ...columnData }
  );

  const result = Object.values(initialValues).filter(
    item =>
      item?.id && item.value !== COLUMN && item.value !== STATIC && item.value !== NOT_PROVIDED
  );

  const withDisabled = result.reduce(
    (acc, curr) => ({
      ...acc,
      ...curr?.options
        ?.find(option => option?.requires?.length > 0)
        ?.requires?.reduce(
          (a, c) => ({
            ...a,
            [c]: {
              ...initialValues[c],
              disabled: true,
            },
          }),
          {}
        ),
    }),
    {}
  );

  return { ...initialValues, ...withDisabled };
};
